import { useEffect, useState } from 'react';
import { useAuth } from '../../AuthContext.tsx';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CalendarEvent, Player } from '../../api/types';
import { configService, liveMatchService, participantService, playerService } from '../../api/service.ts';
import { LiveMatchState, LivePlayer } from './liveMatchTypes.ts';
import { LiveMatchProvider, clearPersistedState, loadPersistedState, useLiveMatch } from './LiveMatchContext.tsx';
import CallupSelectionStep from './steps/CallupSelectionStep.tsx';
import StrategySelectionStep from './steps/StrategySelectionStep.tsx';
import ServiceSelectionStep from './steps/ServiceSelectionStep.tsx';
import LineupSetupStep from './steps/LineupSetupStep.tsx';
import LiveGameScreen from './game/LiveGameScreen.tsx';

// id dei nostri giocatori nel live di un'amichevole: distinti dagli id dei partecipanti avversari
const FRIENDLY_OUR_ID_OFFSET = 1_000_000;

// ─── Props ───────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  event: CalendarEvent | null;
  onClose: () => void;
  onSaveSuccess?: () => void;
}

const STEP_LABELS = ['Convocazioni', 'Formato', 'Servizio', 'Formazione', 'Partita'];

// ─── Inner component ─────────────────────────────────────────────────────────

function LiveMatchInner({ event, onSaveSuccess }: { event: CalendarEvent; onSaveSuccess?: () => void }) {
  const { state, dispatch } = useLiveMatch();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blockedByRemoteSession, setBlockedByRemoteSession] = useState(false);

  // Stato per il dialog di scelta riprendi/nuova
  const [savedState, setSavedState] = useState<LiveMatchState | null>(null);
  const [askResume, setAskResume] = useState(false);

  // Crea la sessione BE non appena strategy e servingTeam sono entrambi noti (e sessione non ancora esistente)
  useEffect(() => {
    if (state.sessionId === null && state.strategy !== null && state.servingTeam !== null && state.eventId !== 0) {
      liveMatchService
        .startOrResume(state.eventId, state.strategy, state.servingTeam)
        .then((session) => dispatch({ type: 'SET_SESSION_ID', payload: session.id }))
        .catch((err) => console.error('Failed to create live match session:', err));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sessionId, state.strategy, state.servingTeam]);

  async function loadFresh(force = false) {
    const [teamsWithPlayers, ourTeamName, allEntityPlayers] = await Promise.all([
      participantService.getTeamsWithPlayers(),
      configService.getTeamName(),
      playerService.getPlayers(),
    ]);

    const homeId = event.teamAId;
    const awayId = event.teamBId;
    const homeTeam = teamsWithPlayers.find((t) => t.teamId === homeId);
    const awayTeam = teamsWithPlayers.find((t) => t.teamId === awayId);

    function toPlayers(detail: typeof homeTeam, isOurTeam: boolean, entityPlayers: Player[]): LivePlayer[] {
      if (!detail) return [];
      return detail.players.map((p) => {
        const entity = isOurTeam
          ? entityPlayers.find((ep) => ep.firstName === p.firstName && ep.lastName === p.lastName)
          : undefined;
        return {
          id: p.id,
          firstName: p.firstName,
          lastName: p.lastName,
          shirtNumber: p.shirtNumber,
          gender: p.gender ?? 'M',
          roles: [],
          isOurTeam,
          playerEntityId: entity?.id,
        };
      });
    }

    // Determina qual è la nostra squadra: prima per count di match con i giocatori nel DB,
    // poi (a parità) per confronto nome case-insensitive
    function countEntityMatches(detail: typeof homeTeam): number {
      if (!detail) return 0;
      return detail.players.filter((p) =>
        allEntityPlayers.some((ep) => ep.firstName === p.firstName && ep.lastName === p.lastName),
      ).length;
    }
    const homeMatches = countEntityMatches(homeTeam);
    const awayMatches = countEntityMatches(awayTeam);
    const normalize = (s?: string) => (s ?? '').trim().toLowerCase();
    const homeIsOurs = homeMatches !== awayMatches
      ? homeMatches > awayMatches
      : normalize(homeTeam?.teamName) === normalize(ourTeamName);

    // Amichevole: la nostra squadra è la rosa della stagione (i partecipanti del torneo possono non esserci);
    // l'avversario è la sua lista del 2.32 se scelto dall'elenco, altrimenti giocatori generici numerati
    if (event.type === 'FRIENDLY') {
      const ourSide: 'HOME' | 'AWAY' = event.homeGame === false ? 'AWAY' : 'HOME';
      const opponentDetail = teamsWithPlayers.find((t) => t.teamId === event.teamBId);
      const ourPlayers: LivePlayer[] = allEntityPlayers.map((p) => ({
        id: FRIENDLY_OUR_ID_OFFSET + p.id,
        firstName: p.firstName,
        lastName: p.lastName,
        // -1 = numero non ancora assegnato (0 è una maglia valida)
        shirtNumber: p.shirtNumber ?? -1,
        gender: (p.gender === 'F' ? 'F' : 'M') as 'M' | 'F',
        roles: [],
        isOurTeam: true,
        playerEntityId: p.id,
      }));
      const opponentPlayers: LivePlayer[] = opponentDetail
        ? toPlayers(opponentDetail, false, allEntityPlayers)
        : Array.from({ length: 14 }, (_, i) => ({
            id: -(i + 1),
            firstName: 'Giocatore',
            lastName: `#${i + 1}`,
            shirtNumber: i + 1,
            gender: 'M' as const,
            roles: [],
            isOurTeam: false,
          }));
      const opponentName = event.opponentName ?? opponentDetail?.teamName ?? 'Avversario';
      dispatch({
        type: 'INIT',
        payload: {
          eventId: event.eventId,
          eventDate: event.date,
          homeTeamName: ourSide === 'HOME' ? ourTeamName : opponentName,
          awayTeamName: ourSide === 'HOME' ? opponentName : ourTeamName,
          homeTeamId: 0,
          awayTeamId: 0,
          ourTeamSide: ourSide,
          sessionId: null,
          strategy: null,
          servingTeam: null,
          homeShirtColor: '#1565C0',
          awayShirtColor: '#B71C1C',
          homeAllPlayers: ourSide === 'HOME' ? ourPlayers : opponentPlayers,
          awayAllPlayers: ourSide === 'HOME' ? opponentPlayers : ourPlayers,
          homeCallup: [],
          awayCallup: [],
        },
      });
    } else dispatch({
      type: 'INIT',
      payload: {
        eventId: event.eventId,
        eventDate: event.date,
        homeTeamName: homeTeam?.teamName ?? event.title,
        awayTeamName: awayTeam?.teamName ?? 'Avversario',
        homeTeamId: homeId ?? 0,
        awayTeamId: awayId ?? 0,
        ourTeamSide: homeIsOurs ? 'HOME' : 'AWAY',
        sessionId: null,
        strategy: null,
        servingTeam: null,
        homeShirtColor: '#1565C0',
        awayShirtColor: '#B71C1C',
        homeAllPlayers: toPlayers(homeTeam, homeIsOurs, allEntityPlayers),
        awayAllPlayers: toPlayers(awayTeam, !homeIsOurs, allEntityPlayers),
        homeCallup: [],
        awayCallup: [],
      },
    });

    const session = await liveMatchService.getByEvent(event.eventId);
    if (!force && session && session.status === 'IN_PROGRESS') {
      setBlockedByRemoteSession(true);
      setError('Partita già in corso su un altro dispositivo. Riprendi dal browser originale.');
      return;
    }
    if (session) dispatch({ type: 'SET_SESSION_ID', payload: session.id });
  }

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const saved = loadPersistedState(event.eventId);
        if (saved && saved.currentPhase !== 'MATCH_END') {
          // Non ripristina automaticamente: chiede all'utente
          setSavedState(saved);
          setAskResume(true);
          setLoading(false);
          return;
        }
        await loadFresh();
      } catch (err) {
        setError('Errore nel caricamento dei dati. Riprova.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.eventId]);

  async function handleResume() {
    if (savedState) {
      const backendSession = await liveMatchService.getByEvent(event.eventId);
      if (backendSession && backendSession.status === 'IN_PROGRESS' && savedState.sessionId !== backendSession.id) {
        // localStorage stale (da un altro browser o da un'altra sessione) — blocca
        setAskResume(false);
        setSavedState(null);
        setBlockedByRemoteSession(true);
        setError('Partita già in corso su un altro dispositivo. Riprendi dal browser originale.');
        return;
      }
      dispatch({ type: 'RESTORE', payload: { ...savedState, eventDate: savedState.eventDate || event.date } });
    }
    setAskResume(false);
    setSavedState(null);
  }

  async function handleNewGame() {
    setAskResume(false);
    setSavedState(null);
    clearPersistedState(event.eventId);
    setLoading(true);
    try {
      await loadFresh();
    } catch {
      setError('Errore nel caricamento dei dati. Riprova.');
    } finally {
      setLoading(false);
    }
  }

  async function handleForceStart() {
    setError(null);
    setBlockedByRemoteSession(false);
    clearPersistedState(event.eventId);
    setLoading(true);
    try {
      await loadFresh(true);
    } catch {
      setError('Errore nel caricamento dei dati. Riprova.');
    } finally {
      setLoading(false);
    }
  }

  // ─── Navigazione lineup ─────────────────────────────────────────────────

  function handleLineupConfirm() {
    if (state.lineupSetupSide === 'HOME') {
      dispatch({ type: 'NEXT_LINEUP_SIDE' });
    } else {
      dispatch({ type: 'NEXT_LINEUP_SIDE' });
      dispatch({ type: 'START_MATCH' });
    }
  }

  function handleLineupBack() {
    if (state.lineupSetupSide === 'AWAY') {
      dispatch({ type: 'GO_TO_HOME_LINEUP' });
    } else {
      dispatch({ type: 'GO_TO_SERVICE' });
    }
  }

  // ─── Step index ─────────────────────────────────────────────────────────

  const stepIndex = ({
    CALLUP_SELECTION: 0,
    STRATEGY_SELECTION: 1,
    SERVICE_SELECTION: 2,
    LINEUP_SETUP: 3,
    IN_GAME: 4,
    SET_END_SUMMARY: 4,
    MATCH_END: 4,
  } as Record<string, number>)[state.currentPhase] ?? 0;

  const isGamePhase = state.currentPhase === 'IN_GAME'
    || state.currentPhase === 'SET_END_SUMMARY'
    || state.currentPhase === 'MATCH_END';
  const isLineupPhase = state.currentPhase === 'LINEUP_SETUP';

  // ─── Render ─────────────────────────────────────────────────────────────

  // Dialog riprendi / nuova partita
  if (askResume) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" mb={1}>Partita in corso trovata</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Esiste una sessione salvata per questa partita. Vuoi riprenderla o iniziarne una nuova?
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="contained" color="warning" onClick={handleResume}>
            Riprendi gara
          </Button>
          <Button variant="outlined" color="error" onClick={handleNewGame}>
            Nuova partita
          </Button>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" mb={3}>{error}</Typography>
        {blockedByRemoteSession && isAdmin && (
          <Button variant="outlined" color="warning" onClick={handleForceStart}>
            Forza avvio nuova sessione
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={(isGamePhase || isLineupPhase) ? { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' } : undefined}>
      {!isGamePhase && (
        <Box sx={{ px: 2, pt: 1, pb: 2, flexShrink: 0 }}>
          <Stepper activeStep={stepIndex} alternativeLabel>
            {STEP_LABELS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Box>
      )}

      {state.currentPhase === 'CALLUP_SELECTION' && (
        <CallupSelectionStep onConfirm={() => dispatch({ type: 'GO_TO_STRATEGY' })} />
      )}
      {state.currentPhase === 'STRATEGY_SELECTION' && (
        <StrategySelectionStep onBack={() => dispatch({ type: 'GO_TO_CALLUP' })} />
      )}
      {state.currentPhase === 'SERVICE_SELECTION' && (
        <ServiceSelectionStep
          onBack={state.currentSet === 1 ? () => dispatch({ type: 'GO_TO_STRATEGY' }) : undefined}
        />
      )}
      {state.currentPhase === 'LINEUP_SETUP' && (
        <LineupSetupStep onBack={handleLineupBack} onConfirm={handleLineupConfirm} />
      )}
      {isGamePhase && <LiveGameScreen onSaveSuccess={onSaveSuccess} />}
    </Box>
  );
}

// ─── Outer wrapper ────────────────────────────────────────────────────────────

export default function LiveMatchDialog({ open, event, onClose, onSaveSuccess }: Props) {
  if (!event) return null;

  return (
    <LiveMatchProvider>
      <Dialog open={open} onClose={onClose} fullScreen maxWidth={false}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
          <Typography variant="h6" component="span" noWrap sx={{ flex: 1 }}>
            {event.title ?? 'Tabellone live'}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <LiveMatchInner event={event} onSaveSuccess={onSaveSuccess} />
        </DialogContent>
      </Dialog>
    </LiveMatchProvider>
  );
}
